import { buildLoginUrl, buildLogoutUrl, encodeState, decodeStateNonce } from './hostedUi';
import { createPkcePair } from './pkce';
import type { CognitoTokenFailure, CognitoTokenResult } from './cognitoClient';
import type { CookieStore } from './cookieStore';
import type { TokenStore } from './tokenStore';
import type { VerifierStore, VerifierRecord } from './verifierStore';
import type { ChannelFactory, BroadcastPort } from './logoutBroadcast';
import { LOGOUT_BROADCAST_MESSAGE } from './logoutBroadcast';
import type { RenewalResult } from './renewalPolicy';
import { CALLBACK_PATH, LOGOUT_PATH } from './routes';
import type { AuthConfig, CognitoUnavailableCode, MintedOutcome, RenewalTrigger, SessionOutcome } from './types';

export interface CognitoClientLike {
  exchangeCode: (params: { code: string; codeVerifier: string; redirectUri: string }) => Promise<CognitoTokenResult>;
  refresh: (params: { refreshToken: string }) => Promise<CognitoTokenResult>;
  revoke: (params: { token: string }) => Promise<void>;
}

export interface AmsExchangeClientLike {
  exchange: (params: { idToken: string }) => Promise<SessionOutcome>;
}

export interface RenewalPolicyLike {
  arm: (minted: MintedOutcome, onFire: () => void) => void;
  request: (trigger: RenewalTrigger, attempt: () => Promise<SessionOutcome>) => Promise<RenewalResult>;
  stop: () => void;
}

export type BeginLoginResult = { kind: 'redirect'; url: string } | Extract<SessionOutcome, { kind: 'unavailable' }>;

// Narrows AuthSession to the surface consumers depend on: property-typed (not method-typed) so
// tests can build plain-object doubles without unbound-method casts.
export interface AuthSessionLike {
  bootstrap: () => Promise<SessionOutcome>;
  beginLogin: (statePath: string) => Promise<BeginLoginResult>;
  completeLogin: (params: { code: string; state: string }) => Promise<SessionOutcome>;
  renew: (params: { trigger: RenewalTrigger }) => Promise<RenewalResult>;
  logout: () => Promise<string>;
  subscribe: (listener: (outcome: SessionOutcome) => void) => () => void;
}

export interface AuthSessionDeps {
  config: AuthConfig;
  cognitoClient: CognitoClientLike;
  amsExchangeClient: AmsExchangeClientLike;
  tokenStore: TokenStore;
  cookieStore: CookieStore;
  verifierStore: VerifierStore;
  renewalPolicy: RenewalPolicyLike;
  channelFactory: ChannelFactory;
  origin: string;
}

function randomNonce(): string {
  return crypto.randomUUID();
}

// Unavailable, not terminal, is deliberate: renewal keeps RenewalPolicy's bounded
// retry-while-valid branch, and the callback path keeps a working "Sign in again"
// affordance, on provider-side config faults, while source and code stay truthful
// to the underlying Cognito error for telemetry.
//
// unauthorized_client, invalid_scope, and invalid_request are terminal instead on the
// hosted-UI redirect leg (hostedUi.ts's TERMINAL_COGNITO_CODES). The legs differ: here
// a still-valid refresh token makes leniency correct, whereas on the redirect leg there
// is no session left to preserve.
const TOKEN_ENDPOINT_ERROR_CODES = new Set<string>([
  'invalid_client',
  'unauthorized_client',
  'invalid_scope',
  'invalid_request',
  'unsupported_grant_type',
]);

function mapTokenEndpointError(error: string): SessionOutcome {
  if (TOKEN_ENDPOINT_ERROR_CODES.has(error)) {
    return { kind: 'unavailable', source: 'cognito', code: error as CognitoUnavailableCode };
  }
  if (error === 'server_error' || error === 'temporarily_unavailable') {
    return { kind: 'unavailable', source: 'cognito', code: error };
  }
  return { kind: 'unavailable', source: 'transport', code: 'network' };
}

export class AuthSession {
  private readonly config: AuthConfig;

  private readonly cognitoClient: CognitoClientLike;

  private readonly amsExchangeClient: AmsExchangeClientLike;

  private readonly tokenStore: TokenStore;

  private readonly cookieStore: CookieStore;

  private readonly verifierStore: VerifierStore;

  private readonly renewalPolicy: RenewalPolicyLike;

  private readonly origin: string;

  private readonly channel: BroadcastPort;

  private readonly listeners = new Set<(outcome: SessionOutcome) => void>();

  // Bumped by logout(), a sibling-tab broadcast, and dispose() so a bootstrap or renewal
  // already in flight cannot rewrite the cookie/token store or publish a stale outcome
  // once any of them has torn the session down.
  private generation = 0;

  constructor(deps: AuthSessionDeps) {
    this.config = deps.config;
    this.cognitoClient = deps.cognitoClient;
    this.amsExchangeClient = deps.amsExchangeClient;
    this.tokenStore = deps.tokenStore;
    this.cookieStore = deps.cookieStore;
    this.verifierStore = deps.verifierStore;
    this.renewalPolicy = deps.renewalPolicy;
    this.origin = deps.origin;
    this.channel = deps.channelFactory();
    this.channel.onmessage = () => {
      this.generation += 1;
      this.clearLocalState();
      this.renewalPolicy.stop();
      this.publish({ kind: 'signedOut' });
    };
  }

  subscribe(listener: (outcome: SessionOutcome) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  async bootstrap(): Promise<SessionOutcome> {
    const generation = this.generation;
    const outcome = await this.refreshThenExchange();
    if (outcome.kind === 'minted') {
      this.armRenewal(generation, outcome);
    }
    return outcome;
  }

  async beginLogin(statePath: string): Promise<BeginLoginResult> {
    try {
      const { verifier, challenge } = await createPkcePair();
      const nonce = randomNonce();
      this.verifierStore.put({ verifier, nonce });
      return {
        kind: 'redirect',
        url: buildLoginUrl({
          hostedUiUrl: this.config.hostedUiUrl,
          clientId: this.config.clientId,
          redirectUri: `${this.origin}${CALLBACK_PATH}`,
          state: encodeState({ path: statePath, nonce }),
          codeChallenge: challenge,
        }),
      };
    } catch (error) {
      console.error('AMS auth failed to prepare the hosted-UI login redirect', error);
      return { kind: 'unavailable', source: 'storage', code: 'inaccessible' };
    }
  }

  async completeLogin(params: { code: string; state: string }): Promise<SessionOutcome> {
    const generation = this.generation;
    let record: VerifierRecord | null;
    try {
      record = this.verifierStore.take();
    } catch (error) {
      console.error('AMS auth verifier store failed to read the PKCE record', error);
      return { kind: 'unavailable', source: 'storage', code: 'inaccessible' };
    }
    if (record?.nonce !== decodeStateNonce(params.state)) {
      this.clearLocalState();
      return { kind: 'sessionExpired' };
    }

    const result = await this.cognitoClient.exchangeCode({
      code: params.code,
      codeVerifier: record.verifier,
      redirectUri: `${this.origin}${CALLBACK_PATH}`,
    });
    if (!result.ok) {
      return this.mapExchangeCodeFailure(result);
    }

    this.setRefreshTokenIfPresent(generation, result.refreshToken);
    const outcome = await this.amsExchangeClient.exchange({ idToken: result.idToken });
    this.applyOutcomeSideEffects(generation, outcome);
    if (outcome.kind === 'minted') {
      this.armRenewal(generation, outcome);
    }
    return outcome;
  }

  async renew(params: { trigger: RenewalTrigger }): Promise<RenewalResult> {
    const generation = this.generation;
    const result = await this.renewalPolicy.request(params.trigger, () => this.refreshThenExchange());
    if (result.status === 'renewed') {
      this.publish(result.minted, generation);
    } else if (result.status === 'resolved') {
      this.publish(result.outcome, generation);
    }
    return result;
  }

  logout(): Promise<string> {
    const refreshToken = this.cookieStore.get();
    this.generation += 1;
    this.clearLocalState();
    this.renewalPolicy.stop();
    this.channel.postMessage(LOGOUT_BROADCAST_MESSAGE);
    if (refreshToken) {
      this.cognitoClient.revoke({ token: refreshToken }).catch((error: unknown) => {
        console.error('AMS token revocation failed', error);
      });
    }
    return Promise.resolve(
      buildLogoutUrl({
        hostedUiUrl: this.config.hostedUiUrl,
        clientId: this.config.clientId,
        logoutUri: `${this.origin}${LOGOUT_PATH}`,
      }),
    );
  }

  dispose(): void {
    this.generation += 1;
    this.renewalPolicy.stop();
    this.channel.close();
  }

  private async refreshThenExchange(): Promise<SessionOutcome> {
    const generation = this.generation;
    const refreshToken = this.cookieStore.get();
    if (!refreshToken) {
      return { kind: 'sessionExpired' };
    }

    const result = await this.cognitoClient.refresh({ refreshToken });
    if (!result.ok) {
      const outcome = this.mapRefreshFailure(result);
      this.applyOutcomeSideEffects(generation, outcome);
      return outcome;
    }

    this.setRefreshTokenIfPresent(generation, result.refreshToken);
    const outcome = await this.amsExchangeClient.exchange({ idToken: result.idToken });
    this.applyOutcomeSideEffects(generation, outcome);
    return outcome;
  }

  private isCurrent(generation: number): boolean {
    return generation === this.generation;
  }

  private setRefreshTokenIfPresent(generation: number, refreshToken: string | undefined): void {
    if (refreshToken && this.isCurrent(generation)) {
      this.cookieStore.set(refreshToken);
    }
  }

  private applyOutcomeSideEffects(generation: number, outcome: SessionOutcome): void {
    if (!this.isCurrent(generation)) {
      return;
    }
    if (outcome.kind === 'minted') {
      this.tokenStore.set(outcome.accessToken);
      return;
    }
    if (outcome.kind === 'terminal' || outcome.kind === 'sessionExpired') {
      this.clearLocalState();
    }
  }

  private clearLocalState(): void {
    this.cookieStore.clear();
    this.tokenStore.clear();
  }

  private armRenewal(generation: number, outcome: MintedOutcome): void {
    if (!this.isCurrent(generation)) {
      return;
    }
    this.renewalPolicy.arm(outcome, () => {
      void this.renew({ trigger: 'timer' });
    });
  }

  private publish(outcome: SessionOutcome, generation: number = this.generation): void {
    if (generation !== this.generation) {
      return;
    }
    this.listeners.forEach((listener) => listener(outcome));
  }

  private mapRefreshFailure(result: CognitoTokenFailure): SessionOutcome {
    return this.mapCognitoFailure(result, () => ({ kind: 'sessionExpired' }));
  }

  private mapExchangeCodeFailure(result: CognitoTokenFailure): SessionOutcome {
    return this.mapCognitoFailure(result, () => ({ kind: 'unavailable', source: 'cognito', code: 'code_exchange_rejected' }));
  }

  private mapCognitoFailure(result: CognitoTokenFailure, onInvalidGrant: () => SessionOutcome): SessionOutcome {
    if (result.kind === 'transport') {
      return { kind: 'unavailable', source: 'transport', code: result.code };
    }
    if (result.error === 'invalid_grant') {
      return onInvalidGrant();
    }
    return mapTokenEndpointError(result.error);
  }
}
